import { useCallback, useMemo } from 'react';

import {
  type Cart,
  type ConsumerCheckoutCartsResponse,
  useUpdateConsumerCheckout,
} from '@givebacks/api/store';
import { arrayHasLength } from '@givebacks/utils/array';
import { formatList } from '@givebacks/utils/strings';
import { ProcessingFeesForm } from 'core/Components';
import { Card } from 'ui';

import { useCausesQueries } from '../hooks/useCausesQueries';
import classes from './CheckoutFees.module.css';

type CheckoutProcessingFeesFormProps = {
  checkoutId: string;
  cartIds: string[];
  isCoveringFees: boolean;
  checkoutFee?: number;
  pledgeRequired?: boolean;
  carts: Cart[];
};

const CheckoutProcessingFeesForm = ({
  cartIds,
  checkoutId,
  carts,
  isCoveringFees,
  ...props
}: CheckoutProcessingFeesFormProps) => {
  const causesThatHaveAskAtCheckout = useMemo(() => {
    return carts.filter((cart) => cart.ask_at_checkout).map((cart) => cart.cause_id);
  }, [carts]);
  const { mutate: updateCheckoutFees } = useUpdateConsumerCheckout();

  const { causeNames } = useCausesQueries(causesThatHaveAskAtCheckout);

  const handleChange = useCallback(
    (data: boolean) => {
      updateCheckoutFees({
        checkout: {
          processing_fees: data ? 'pass_on_fees' : 'absorb_fees',
        },
        checkoutId: checkoutId,
        cartIds: cartIds,
      });
    },
    [cartIds, checkoutId, updateCheckoutFees]
  );

  const causesNamesString = useMemo(() => {
    if (!causeNames.length) return '';
    return formatList(causeNames);
  }, [causeNames]);

  if (!arrayHasLength(causesThatHaveAskAtCheckout)) return null;
  return (
    <Card.Section inheritPadding className={classes.CheckoutFees_section}>
      <ProcessingFeesForm
        {...props}
        onChange={handleChange}
        causeName={causesNamesString}
        isCoveringFees={isCoveringFees}
      />
    </Card.Section>
  );
};

const CheckoutProcessingFeesFormGuard = ({
  checkoutResponse,
  cartIds,
}: {
  checkoutResponse?: ConsumerCheckoutCartsResponse;
  cartIds: string[];
}) => {
  const askAtCheckout = useMemo(() => {
    return checkoutResponse?.checkout.ask_at_checkout;
  }, [checkoutResponse]);

  const carts = useMemo(() => {
    return checkoutResponse?.checkout.carts ?? [];
  }, [checkoutResponse]);

  if (!askAtCheckout || !checkoutResponse?.checkout.uuid) return null;
  return (
    <CheckoutProcessingFeesForm
      cartIds={cartIds}
      checkoutId={checkoutResponse?.checkout.uuid}
      isCoveringFees={checkoutResponse?.checkout.processing_fees === 'pass_on_fees'}
      checkoutFee={checkoutResponse?.checkout.fee}
      pledgeRequired={checkoutResponse?.checkout.pledge_required}
      carts={carts}
    />
  );
};

export { CheckoutProcessingFeesFormGuard as CheckoutProcessingFeesForm };
